import * as React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { Avatar, Button, Checkbox, Descriptions, Empty, Input, InputNumber, Modal, notification, Popover, Select, Spin, Statistic, Tag, Tooltip, Typography } from 'antd';
import { CaretDownOutlined, CaretUpOutlined, CheckOutlined, CloseOutlined, DeleteOutlined, DownloadOutlined, EditOutlined, PlusOutlined, SettingOutlined } from '@ant-design/icons';
import { PageContainer } from '@ant-design/pro-layout';
import { useNavigate, useParams } from 'react-router-dom';
import axiosApiInstance from '../../../api/axiosClient';
import {
    useCampaignQuery, useAppendCreators, useUpdateCampaign, useUpdateCreatorMeta,
    useAddCreatorDeliverable, useUpdateCreatorDeliverable, useDeleteCreatorDeliverable,
} from './queryHooks';
import type { Campaign, CampaignCreator, CampaignCreatorDeliverable, DeliverablePlatform, DeliverablePostType } from './types';
import { YT_ICON, IG_ICON, TT_ICON, formatFollowers } from '../Creators/utils';
import CreatorDetailContent from '../Creators/CreatorDetailContent';

/** Opens creator detail as a centered modal (same style as the Creators page). */
const CreatorDrawer: React.FC<{
    creatorId: number | null;
    onClose: () => void;
}> = ({ creatorId, onClose }) => (
    <Modal
        open={creatorId !== null}
        onCancel={onClose}
        width={1000}
        title="Creator Details"
        footer={null}
        destroyOnClose
    >
        {creatorId !== null && (
            <React.Suspense fallback={<div style={{ textAlign: 'center', paddingTop: 60 }}><Spin /></div>}>
                <CreatorDetailContent id={String(creatorId)} />
            </React.Suspense>
        )}
    </Modal>
);

const CAMPAIGN_CREATOR_COLS = [
    { key: 'approved',     label: 'Approved' },
    { key: 'pillar',       label: 'Pillar' },
    { key: 'yt',           label: 'YT Followers' },
    { key: 'ig',           label: 'IG Followers' },
    { key: 'tt',           label: 'TT Followers' },
    { key: 'budget',       label: 'Budget (€)' },
    { key: 'comments',     label: 'Comments' },
    { key: 'deliverables', label: 'Deliverables' },
] as const;
type ColKey = typeof CAMPAIGN_CREATOR_COLS[number]['key'];
const COLS_STORAGE_KEY = 'campaign-view-creator-cols';
const DEFAULT_VISIBLE: ColKey[] = ['approved', 'pillar', 'yt', 'ig', 'tt', 'budget', 'comments', 'deliverables'];

const PLATFORM_ICON: Record<string, React.ReactNode> = { yt: YT_ICON, ig: IG_ICON, tt: TT_ICON };
const PLATFORM_OPTIONS = [
    { label: 'YouTube', value: 'yt' },
    { label: 'Instagram', value: 'ig' },
    { label: 'TikTok', value: 'tt' },
];
const POST_TYPE_OPTIONS = [
    { label: 'Photo', value: 'image' },
    { label: 'Video', value: 'video' },
    { label: 'Text', value: 'text' },
    { label: 'Story', value: 'story' },
    { label: 'Stream', value: 'stream' },
];

const statusColor = (s?: string) => {
    switch (s) {
        case 'spec':
            return 'gold';
        case 'active':
            return 'green';
        case 'completed':
            return 'blue';
        case 'canceled':
        case 'cancelled':
            return 'red';
        default:
            return 'default';
    }
};

const thStyle: React.CSSProperties = {
    padding: '6px 10px',
    textAlign: 'left',
    fontSize: 12,
    fontWeight: 600,
    color: '#888',
    whiteSpace: 'nowrap',
};

const tdStyle: React.CSSProperties = {
    padding: '8px 10px',
    verticalAlign: 'middle',
};

type DraftDeliverable = {
    /** Undefined = new row not yet saved */
    id?: number;
    platform: DeliverablePlatform;
    post_type: DeliverablePostType;
    quantity: number;
    completed: number;
};

/** Inline deliverable chips + modal editor for one creator row. */
const CreatorDeliverables: React.FC<{
    campaignId: number;
    creatorId: number;
    deliverables: CampaignCreatorDeliverable[];
    onSaved?: () => void;
}> = ({ campaignId, creatorId, deliverables, onSaved }) => {
    const addDeliverable = useAddCreatorDeliverable();
    const updateDeliverable = useUpdateCreatorDeliverable();
    const deleteDeliverable = useDeleteCreatorDeliverable();

    // Optimistic local copy — updates instantly on click, syncs from prop when
    // the prop changes (e.g. after a successful save/invalidation).
    const [local, setLocal] = useState<CampaignCreatorDeliverable[]>(deliverables);
    useEffect(() => { setLocal(deliverables); }, [deliverables]);

    const [modalOpen, setModalOpen] = useState(false);
    const [rows, setRows] = useState<DraftDeliverable[]>([]);
    const [saving, setSaving] = useState(false);

    const openModal = () => {
        setRows(local.map((d) => ({
            id: d.id, platform: d.platform as DeliverablePlatform,
            post_type: d.post_type as DeliverablePostType,
            quantity: d.quantity, completed: d.completed,
        })));
        setModalOpen(true);
    };

    const updateRow = (i: number, patch: Partial<DraftDeliverable>) =>
        setRows((prev) => prev.map((r, j) => (j === i ? { ...r, ...patch } : r)));
    const removeRow = (i: number) =>
        setRows((prev) => prev.filter((_, j) => j !== i));
    const addRow = () =>
        setRows((prev) => [...prev, { platform: 'yt', post_type: 'video', quantity: 1, completed: 0 }]);

    const handleSave = async () => {
        setSaving(true);
        try {
            const keptIds = new Set(rows.filter((r) => r.id !== undefined).map((r) => r.id!));

            // 1. Delete removed rows
            for (const d of local) {
                if (!keptIds.has(d.id)) {
                    await deleteDeliverable.mutateAsync({ campaignId, creatorId, deliverableId: d.id });
                }
            }

            // 2. Update changed rows
            for (const row of rows) {
                if (row.id === undefined) continue;
                const orig = local.find((d) => d.id === row.id);
                if (orig && orig.quantity !== row.quantity) {
                    await updateDeliverable.mutateAsync({
                        campaignId, creatorId,
                        deliverableId: row.id,
                        completed: orig.completed,
                        quantity: row.quantity,
                    });
                }
            }

            // 3. Add new rows
            for (const row of rows) {
                if (row.id !== undefined) continue;
                await addDeliverable.mutateAsync({
                    campaignId, creatorId,
                    platform: row.platform, post_type: row.post_type, quantity: row.quantity,
                });
            }

            setModalOpen(false);
            onSaved?.();
        } catch {
            notification.error({ message: 'Failed to save deliverables' });
        } finally {
            setSaving(false);
        }
    };

    const handleToggleCompleted = async (d: CampaignCreatorDeliverable) => {
        const next = d.completed >= d.quantity ? 0 : d.completed + 1;
        // Optimistic update — show the new value immediately
        setLocal((prev) =>
            prev.map((item) => item.id === d.id ? { ...item, completed: next } : item)
        );
        try {
            await updateDeliverable.mutateAsync({
                campaignId, creatorId,
                deliverableId: d.id,
                completed: next,
                quantity: d.quantity,
            });
        } catch {
            // Revert on failure
            setLocal((prev) =>
                prev.map((item) => item.id === d.id ? { ...item, completed: d.completed } : item)
            );
            notification.error({ message: 'Failed to update deliverable' });
        }
    };

    return (
        <>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'flex-start' }}>
                {local.map((d) => {
                    const done = d.completed >= d.quantity;
                    return (
                        <div
                            key={d.id}
                            style={{
                                display: 'inline-flex', flexDirection: 'column', alignItems: 'flex-start',
                                gap: 2, cursor: 'default', userSelect: 'none',
                            }}
                        >
                            {/* progress chip */}
                            <span
                                onClick={() => handleToggleCompleted(d)}
                                style={{
                                    display: 'inline-flex', alignItems: 'center', gap: 3,
                                    cursor: 'pointer',
                                    color: done ? '#52c41a' : '#555',
                                    fontWeight: done ? 600 : 400,
                                    fontSize: 13,
                                }}
                            >
                                {PLATFORM_ICON[d.platform]}
                                <span>{d.completed}/{d.quantity}</span>
                            </span>
                        </div>
                    );
                })}
                {local.length > 0 && (() => {
                    const doneCount = local.filter((d) => d.completed >= d.quantity).length;
                    return (
                        <span style={{ fontSize: 11, color: '#aaa', marginLeft: 2 }}>
                            {doneCount}/{local.length} done
                        </span>
                    );
                })()}
                <Button
                    type="text"
                    size="small"
                    icon={<PlusOutlined />}
                    onClick={openModal}
                    style={{ padding: '0 4px', color: '#aaa' }}
                />
            </div>

            <Modal
                open={modalOpen}
                title="Deliverables"
                onCancel={() => setModalOpen(false)}
                destroyOnClose
                width={500}
                footer={
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Button icon={<PlusOutlined />} onClick={addRow}>
                            Add New
                        </Button>
                        <Button type="primary" loading={saving} onClick={handleSave}>
                            Save
                        </Button>
                    </div>
                }
            >
                {rows.length === 0 && (
                    <Empty description="No deliverables yet" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                )}
                {rows.map((row, i) => (
                    <div key={i} style={{ marginBottom: 12, borderBottom: '1px solid #f0f0f0', paddingBottom: 10 }}>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 100px 40px', gap: '6px 10px', alignItems: 'center' }}>
                            <Select
                                value={row.platform}
                                options={PLATFORM_OPTIONS}
                                onChange={(v) => updateRow(i, { platform: v as DeliverablePlatform })}
                                style={{ width: '100%' }}
                            />
                            <Select
                                value={row.post_type}
                                options={POST_TYPE_OPTIONS}
                                onChange={(v) => updateRow(i, { post_type: v as DeliverablePostType })}
                                style={{ width: '100%' }}
                            />
                            <InputNumber
                                min={1}
                                max={99}
                                value={row.quantity}
                                onChange={(v) => updateRow(i, { quantity: v ?? 1 })}
                                style={{ width: '100%' }}
                            />
                            <Button
                                type="text"
                                danger
                                icon={<DeleteOutlined />}
                                onClick={() => removeRow(i)}
                            />
                        </div>
                    </div>
                ))}
            </Modal>
        </>
    );
};

/**
 * CampaignView — read-only detail page for a campaign.
 *
 * Shows campaign metadata + the full list of linked creators as rows.
 */
const CampaignView: React.FC = () => {
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();
    const campaignId = id ? Number(id) : undefined;

    const { data: campaign, isLoading, isError, refetch: refetchCampaign } = useCampaignQuery(campaignId);
    const append = useAppendCreators();
    const update = useUpdateCampaign();
    const updateMeta = useUpdateCreatorMeta();

    // Column visibility — persisted to localStorage
    const [visibleCols, setVisibleCols] = useState<ColKey[]>(() => {
        try {
            const stored = localStorage.getItem(COLS_STORAGE_KEY);
            return stored ? (JSON.parse(stored) as ColKey[]) : DEFAULT_VISIBLE;
        } catch { return DEFAULT_VISIBLE; }
    });
    const toggleCol = (key: ColKey) => {
        setVisibleCols((prev) => {
            const next = prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key];
            localStorage.setItem(COLS_STORAGE_KEY, JSON.stringify(next));
            return next;
        });
    };
    const col = (key: ColKey) => visibleCols.includes(key);

    // Per-creator local edits: keyed by creator id
const [metaMap, setMetaMap] = useState<Record<number, { budget: number | null; comments: string; approved: boolean | null; pillar: string }>>({}); 

    // Initialise metaMap from the campaign's creator_metas whenever data arrives
    useEffect(() => {
        if (!campaign?.creator_metas) return;
        const next: Record<number, { budget: number | null; comments: string; approved: boolean | null; pillar: string }> = {};
        for (const m of campaign.creator_metas) {
            next[m.creator_id] = { budget: m.budget ?? null, comments: m.comments ?? '', approved: m.approved ?? null, pillar: m.pillar ?? '' };
        }
        setMetaMap(next);
    }, [campaign?.creator_metas]);

    // Creator search state
    const [creatorSearch, setCreatorSearch] = useState('');
    const [creatorOptions, setCreatorOptions] = useState<{ label: string; value: number }[]>([]);
    const [creatorSearchLoading, setCreatorSearchLoading] = useState(false);

    useEffect(() => {
        const handle = setTimeout(() => {
            setCreatorSearchLoading(true);
            axiosApiInstance
                .get<API.DataResponse<CampaignCreator[]>>('/api/creators/items', {
                    params: {
                        search: creatorSearch,
                        page: 1,
                        page_size: 50,
                        youtube_followers_min: 0,
                        youtube_followers_max: 1000000,
                        instagram_followers_min: 0,
                        instagram_followers_max: 1000000,
                        tiktok_followers_min: 0,
                        tiktok_followers_max: 1000000,
                    }
                })
                .then(({ data }) => {
                    setCreatorOptions(
                        (data?.data ?? []).map((c) => ({
                            label: c.creator || `Creator #${c.id}`,
                            value: c.id,
                        }))
                    );
                })
                .catch((err) => console.error('creator search failed', err))
                .finally(() => setCreatorSearchLoading(false));
        }, 300);
        return () => clearTimeout(handle);
    }, [creatorSearch]);

    const handleAddCreator = async (creatorId: number) => {
        if (!campaign?.id) return;
        try {
            await append.mutateAsync({ id: campaign.id, creatorIds: [creatorId] });
            await refetchCampaign();
            notification.success({ message: 'Creator added', placement: 'bottomRight', duration: 2 });
        } catch {
            notification.error({ message: 'Failed to add creator' });
        }
    };

    const handleRemoveCreator = async (creatorId: number) => {
        if (!campaign?.id) return;
        try {
            await update.mutateAsync({
                id: campaign.id,
                name: campaign.name,
                project: campaign.project,
                notes: campaign.notes,
                brief: campaign.brief,
                budget: campaign.budget,
                deliverables: campaign.deliverables,
                brand_id: campaign.brand_id,
                status: campaign.status,
                start_date: campaign.start_date,
                end_date: campaign.end_date,
                creators: (campaign.creators ?? []).filter((c) => c.id !== creatorId),
            } as Campaign & { id: number });
            notification.success({ message: 'Creator removed', placement: 'bottomRight', duration: 2 });
        } catch {
            notification.error({ message: 'Failed to remove creator' });
        }
    };

    // Summary stats — only count creators explicitly marked approved
    const [sortCol, setSortCol] = useState<'yt' | 'ig' | 'tt' | 'budget' | null>(null);
    const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
    const [drawerCreatorId, setDrawerCreatorId] = useState<number | null>(null);
    const [exporting, setExporting] = useState(false);

    const onExport = async () => {
        if (!campaign?.id) return;
        setExporting(true);
        try {
            const res = await axiosApiInstance.get(`/api/campaigns/items/${campaign.id}/export`, { responseType: 'blob' });
            const disposition = String(res.headers['content-disposition'] ?? '');
            const star = disposition.match(/filename\*=UTF-8''([^;]+)/i);
            const plain = disposition.match(/filename="([^"]+)"/i);
            const filename = star
                ? decodeURIComponent(star[1])
                : plain?.[1] || `${campaign.name}.xlsx`;
            const url = URL.createObjectURL(res.data);
            const link = document.createElement('a');
            link.href = url;
            link.download = filename;
            link.click();
            URL.revokeObjectURL(url);
        } catch {
            notification.error({ message: 'Export failed' });
        } finally {
            setExporting(false);
        }
    };

    // Summary stats — only count creators explicitly marked approved
    const approvedStats = useMemo(() => {
        const approved = (campaign?.creators ?? []).filter(
            (c) => metaMap[c.id]?.approved === true
        );
        let ytFollowers = 0, igFollowers = 0, ttFollowers = 0, totalBudget = 0;
        for (const c of approved) {
            ytFollowers += c.accounts?.find((a) => a.platform?.name === 'YouTube'   || a.platform_id === 1)?.following ?? 0;
            igFollowers += c.accounts?.find((a) => a.platform?.name === 'Instagram' || a.platform_id === 2)?.following ?? 0;
            ttFollowers += c.accounts?.find((a) => a.platform?.name === 'TikTok'    || a.platform_id === 3)?.following ?? 0;
            totalBudget += metaMap[c.id]?.budget ?? 0;
        }
        return { ytFollowers, igFollowers, ttFollowers, totalBudget, count: approved.length };
    }, [campaign?.creators, metaMap]);

    if (isLoading) {
        return (
            <PageContainer title="Campaign">
                <Spin />
            </PageContainer>
        );
    }

    if (isError || !campaign) {
        return (
            <PageContainer title="Campaign">
                <Empty description="Campaign not found" />
            </PageContainer>
        );
    }

    const linkedIds = new Set((campaign.creators ?? []).map((c) => c.id));
    const filteredOptions = creatorOptions.filter((o) => !linkedIds.has(o.value));

    type SortCol = 'yt' | 'ig' | 'tt' | 'budget';
    const handleSort = (col: SortCol) => {
        if (sortCol === col) {
            setSortDir((d) => (d === 'desc' ? 'asc' : 'desc'));
        } else {
            setSortCol(col);
            setSortDir('desc');
        }
    };

    const SortIcon: React.FC<{ col: SortCol }> = ({ col: c }) => (
        <span style={{ marginLeft: 4, opacity: sortCol === c ? 1 : 0.3, fontSize: 10 }}>
            {sortCol === c && sortDir === 'asc' ? <CaretUpOutlined /> : <CaretDownOutlined />}
        </span>
    );

    const rawCreators = campaign.creators ?? [];
    const creators = sortCol
        ? [...rawCreators].sort((a, b) => {
            let va = 0, vb = 0;
            if (sortCol === 'yt') {
                va = a.accounts?.find((acc) => acc.platform?.name === 'YouTube' || acc.platform_id === 1)?.following ?? 0;
                vb = b.accounts?.find((acc) => acc.platform?.name === 'YouTube' || acc.platform_id === 1)?.following ?? 0;
            } else if (sortCol === 'ig') {
                va = a.accounts?.find((acc) => acc.platform?.name === 'Instagram' || acc.platform_id === 2)?.following ?? 0;
                vb = b.accounts?.find((acc) => acc.platform?.name === 'Instagram' || acc.platform_id === 2)?.following ?? 0;
            } else if (sortCol === 'tt') {
                va = a.accounts?.find((acc) => acc.platform?.name === 'TikTok' || acc.platform_id === 3)?.following ?? 0;
                vb = b.accounts?.find((acc) => acc.platform?.name === 'TikTok' || acc.platform_id === 3)?.following ?? 0;
            } else if (sortCol === 'budget') {
                va = metaMap[a.id]?.budget ?? 0;
                vb = metaMap[b.id]?.budget ?? 0;
            }
            return sortDir === 'asc' ? va - vb : vb - va;
          })
        : rawCreators;

    return (
        <>
        <PageContainer
            title={campaign.name}
            onBack={() => navigate('/creators/campaigns')}
            extra={[
                <Button
                    key="export"
                    icon={<DownloadOutlined />}
                    loading={exporting}
                    onClick={onExport}
                >
                    Export
                </Button>,
                <Button
                    key="edit"
                    type="primary"
                    icon={<EditOutlined />}
                    onClick={() => navigate(`/creators/campaigns/${campaign.id}/edit`)}
                >
                    Edit
                </Button>
            ]}
        >
            <Descriptions column={2} bordered size="small">
                <Descriptions.Item label="Name">{campaign.name}</Descriptions.Item>
                <Descriptions.Item label="Status">
                    <Tag color={statusColor(campaign.status)}>{campaign.status || 'spec'}</Tag>
                </Descriptions.Item>
                <Descriptions.Item label="Brand">
                    {campaign.brand?.name ? (
                        <Tag color="geekblue" style={{ margin: 0 }}>{campaign.brand.name}</Tag>
                    ) : (
                        <span style={{ color: '#bbb' }}>—</span>
                    )}
                </Descriptions.Item>
                <Descriptions.Item label="Budget (€)">
                    {campaign.budget != null
                        ? new Intl.NumberFormat('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(campaign.budget)
                        : <span style={{ color: '#bbb' }}>—</span>}
                </Descriptions.Item>
                <Descriptions.Item label="Start Date">
                    {campaign.start_date ? new Date(campaign.start_date).toLocaleDateString() : <span style={{ color: '#bbb' }}>—</span>}
                </Descriptions.Item>
                <Descriptions.Item label="End Date">
                    {campaign.end_date ? new Date(campaign.end_date).toLocaleDateString() : <span style={{ color: '#bbb' }}>—</span>}
                </Descriptions.Item>

                {/* Paid Media */}
                <Descriptions.Item label="Paid Media">
                    {campaign.paid_media
                        ? <Tag color="green">Yes</Tag>
                        : <Tag color="default">No</Tag>}
                </Descriptions.Item>
                {(campaign.paid_media_start || campaign.paid_media_end) && (
                    <Descriptions.Item label="Paid Media Period">
                        {campaign.paid_media_start ? new Date(campaign.paid_media_start).toLocaleDateString() : '?'} – {campaign.paid_media_end ? new Date(campaign.paid_media_end).toLocaleDateString() : '?'}
                    </Descriptions.Item>
                )}

                {/* Competition */}
                {(campaign.competition_start || campaign.competition_end) && (
                    <Descriptions.Item label="Competition Period">
                        {campaign.competition_start ? new Date(campaign.competition_start).toLocaleDateString() : '?'} – {campaign.competition_end ? new Date(campaign.competition_end).toLocaleDateString() : '?'}
                    </Descriptions.Item>
                )}
                {campaign.competition_comments && (
                    <Descriptions.Item label="Competition Comments">
                        <Typography.Text style={{ whiteSpace: 'pre-wrap' }}>{campaign.competition_comments}</Typography.Text>
                    </Descriptions.Item>
                )}

                {/* Deliverable rules */}
                <Descriptions.Item label="Deliverable Rules" span={2}>
                    {(campaign.deliverables2 ?? []).length > 0
                        ? <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                            {(campaign.deliverables2 ?? []).map((d, i) => (
                                <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                                    {PLATFORM_ICON[d.platform]}
                                    <span style={{ fontSize: 12 }}>{d.post_type} ×{d.quantity}</span>
                                </span>
                            ))}
                          </div>
                        : <span style={{ color: '#bbb' }}>—</span>}
                </Descriptions.Item>

                {campaign.brief && (
                    <Descriptions.Item label="Brief" span={2}>
                        <Typography.Paragraph style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
                            {campaign.brief}
                        </Typography.Paragraph>
                    </Descriptions.Item>
                )}
                {campaign.deliverables && (
                    <Descriptions.Item label="Deliverables" span={2}>
                        <Typography.Paragraph style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
                            {campaign.deliverables}
                        </Typography.Paragraph>
                    </Descriptions.Item>
                )}
                <Descriptions.Item label="Updated">
                    {campaign.updated_at ? new Date(campaign.updated_at).toLocaleString() : '-'}
                </Descriptions.Item>
                <Descriptions.Item label="Created">
                    {campaign.created_at ? new Date(campaign.created_at).toLocaleString() : '-'}
                </Descriptions.Item>
            </Descriptions>

            {/* Approved-creator summary */}
            <table style={{
                marginTop: 20, width: '50%', borderCollapse: 'collapse',
                background: '#fafafa', border: '1px solid #f0f0f0', borderRadius: 6,
                fontSize: 12,
            }}>
                <thead>
                    <tr style={{ borderBottom: '1px solid #e8e8e8' }}>
                        <th style={{ ...thStyle, textAlign: 'center', borderRight: '1px solid #f0f0f0' }}>Approved</th>
                        <th style={{ ...thStyle, textAlign: 'center', borderRight: '1px solid #f0f0f0' }}><span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>{YT_ICON} Followers</span></th>
                        <th style={{ ...thStyle, textAlign: 'center', borderRight: '1px solid #f0f0f0' }}><span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>{IG_ICON} Followers</span></th>
                        <th style={{ ...thStyle, textAlign: 'center', borderRight: '1px solid #f0f0f0' }}><span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>{TT_ICON} Followers</span></th>
                        <th style={{ ...thStyle, textAlign: 'center', borderRight: '1px solid #f0f0f0' }}>Creators Budget</th>
                        <th style={{ ...thStyle, textAlign: 'center' }}>DM Budget</th>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td style={{ ...tdStyle, textAlign: 'center', fontWeight: 600, fontSize: 13, borderRight: '1px solid #f0f0f0' }}>{approvedStats.count}</td>
                        <td style={{ ...tdStyle, textAlign: 'center', fontWeight: 600, fontSize: 13, borderRight: '1px solid #f0f0f0' }}>{formatFollowers(approvedStats.ytFollowers)}</td>
                        <td style={{ ...tdStyle, textAlign: 'center', fontWeight: 600, fontSize: 13, borderRight: '1px solid #f0f0f0' }}>{formatFollowers(approvedStats.igFollowers)}</td>
                        <td style={{ ...tdStyle, textAlign: 'center', fontWeight: 600, fontSize: 13, borderRight: '1px solid #f0f0f0' }}>{formatFollowers(approvedStats.ttFollowers)}</td>
                        <td style={{ ...tdStyle, textAlign: 'center', fontWeight: 600, fontSize: 13, borderRight: '1px solid #f0f0f0' }}>€{approvedStats.totalBudget.toLocaleString()}</td>
                        <td style={{ ...tdStyle, textAlign: 'center', fontWeight: 600, fontSize: 13, color: campaign.budget != null && approvedStats.totalBudget > campaign.budget ? '#ff4d4f' : undefined }}>
                            {campaign.budget != null ? `€${(campaign.budget - approvedStats.totalBudget).toLocaleString()}` : '—'}
                        </td>
                    </tr>
                </tbody>
            </table>

            <div style={{ marginTop: 24 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                    <Typography.Title level={5} style={{ margin: 0 }}>
                        Creators ({creators.length})
                    </Typography.Title>
                    <Popover
                        trigger="click"
                        placement="bottomRight"
                        content={
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, minWidth: 160 }}>
                                {CAMPAIGN_CREATOR_COLS.map(({ key, label }) => (
                                    <Checkbox
                                        key={key}
                                        checked={col(key)}
                                        onChange={() => toggleCol(key)}
                                    >
                                        {label}
                                    </Checkbox>
                                ))}
                            </div>
                        }
                    >
                        <Button size="small" icon={<SettingOutlined />}>Columns</Button>
                    </Popover>
                </div>

                <Select
                    showSearch
                    allowClear
                    value={null}
                    placeholder="Search and add a creator…"
                    filterOption={false}
                    loading={creatorSearchLoading || append.isLoading}
                    disabled={append.isLoading}
                    onSearch={setCreatorSearch}
                    options={filteredOptions}
                    onChange={(creatorId: number) => handleAddCreator(creatorId)}
                    notFoundContent={creatorSearchLoading ? 'Searching…' : 'No creators found'}
                    style={{ width: 360, marginBottom: 16 }}
                />

                {creators.length === 0 ? (
                    <Empty description="No creators linked yet" />
                ) : (
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                            <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                                <th style={thStyle}></th>
                                <th style={thStyle}>Creator</th>
                                {col('approved') && <th style={{ ...thStyle, textAlign: 'center' }}>Approved</th>}
                                {col('pillar') && <th style={thStyle}>Pillar</th>}
                                {col('yt') && <th style={{ ...thStyle, textAlign: 'center', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('yt')}>{YT_ICON}<SortIcon col="yt" /></th>}
                                {col('ig') && <th style={{ ...thStyle, textAlign: 'center', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('ig')}>{IG_ICON}<SortIcon col="ig" /></th>}
                                {col('tt') && <th style={{ ...thStyle, textAlign: 'center', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('tt')}>{TT_ICON}<SortIcon col="tt" /></th>}
                                {col('budget') && <th style={{ ...thStyle, cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('budget')}>Total Budget (€)<SortIcon col="budget" /></th>}
                                {col('comments') && <th style={thStyle}>Comments</th>}
                                {col('deliverables') && <th style={thStyle}>Deliverables</th>}
                                <th style={thStyle}></th>
                            </tr>
                        </thead>
                        <tbody>
                            {creators.map((c: CampaignCreator) => {
                                const meta = metaMap[c.id] ?? { budget: null, comments: '', approved: null, pillar: '' };

                                const cycleApproved = (current: boolean | null): boolean | null => {
                                    if (current == null) return true;
                                    if (current === true) return false;
                                    return null;
                                };

                                const imgAccount = c.accounts?.find((a) => a.image);
                                const avatarSrc = imgAccount?.image
                                    ? `data:image/jpeg;base64,${imgAccount.image}`
                                    : undefined;

                                const ytAccount = c.accounts?.find(
                                    (a) => a.platform?.name === 'YouTube' || a.platform_id === 1,
                                );
                                const igAccount = c.accounts?.find(
                                    (a) => a.platform?.name === 'Instagram' || a.platform_id === 2,
                                );
                                const ttAccount = c.accounts?.find(
                                    (a) => a.platform?.name === 'TikTok' || a.platform_id === 3,
                                );
                                const ytFollowers = ytAccount?.following;
                                const igFollowers = igAccount?.following;
                                const ttFollowers = ttAccount?.following;

                                const saveMeta = async (budget: number | null, comments: string, approved: boolean | null, pillar: string) => {
                                    if (!campaign?.id) return;
                                    try {
                                        await updateMeta.mutateAsync({
                                            campaignId: campaign.id,
                                            creatorId: c.id,
                                            budget,
                                            comments,
                                            approved,
                                            pillar,
                                        });
                                    } catch {
                                        notification.error({ message: 'Failed to save creator meta' });
                                    }
                                };

                                return (
                                    <tr key={c.id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                                        {/* avatar */}
                                        <td style={tdStyle}>
                                            <Avatar
                                                src={avatarSrc}
                                                size={34}
                                                style={{ cursor: 'pointer', background: '#d3adf7' }}
                                                onClick={() => setDrawerCreatorId(c.id)}
                                            >
                                                {!avatarSrc && (c.creator?.[0] ?? '?').toUpperCase()}
                                            </Avatar>
                                        </td>
                                        {/* name */}
                                        <td style={tdStyle}>
                                            <Typography.Text
                                                strong
                                                style={{ cursor: 'pointer' }}
                                                onClick={() => setDrawerCreatorId(c.id)}
                                            >
                                                {c.creator || `Creator #${c.id}`}
                                            </Typography.Text>
                                        </td>
                                        {/* approved */}
                                        {col('approved') && <td style={{ ...tdStyle, textAlign: 'center' }}>
                                            <span
                                                onClick={() => {
                                                    const next = cycleApproved(meta.approved);
                                                    const updated = { ...meta, approved: next };
                                                    setMetaMap((prev) => ({ ...prev, [c.id]: updated }));
                                                    saveMeta(updated.budget, updated.comments, updated.approved, updated.pillar);
                                                }}
                                                style={{
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    width: 24,
                                                    height: 24,
                                                    border: `1px solid ${
                                                        meta.approved === true ? '#52c41a'
                                                        : meta.approved === false ? '#ff4d4f'
                                                        : '#d9d9d9'
                                                    }`,
                                                    borderRadius: 4,
                                                    cursor: 'pointer',
                                                    background: meta.approved === true ? '#f6ffed' : meta.approved === false ? '#fff2f0' : '#fff',
                                                    color: meta.approved === true ? '#52c41a' : '#ff4d4f',
                                                    fontSize: 12,
                                                    userSelect: 'none',
                                                }}
                                            >
                                                {meta.approved === true && <CheckOutlined />}
                                                {meta.approved === false && <CloseOutlined />}
                                            </span>
                                        </td>}
                                        {/* pillar */}
                                        {col('pillar') && <td style={tdStyle}>
                                            <Input
                                                size="small"
                                                style={{ width: 140, fontSize: 12 }}
                                                value={meta.pillar}
                                                onChange={(e) =>
                                                    setMetaMap((prev) => ({
                                                        ...prev,
                                                        [c.id]: { ...prev[c.id] ?? { budget: null, comments: '', approved: null }, pillar: e.target.value },
                                                    }))
                                                }
                                                onBlur={() => saveMeta(meta.budget, meta.comments, meta.approved, meta.pillar)}
                                            />
                                        </td>}
                                        {/* YT followers */}
                                        {col('yt') && <td style={{ ...tdStyle, textAlign: 'center' }}>
                                            {ytFollowers != null
                                                ? <a href={ytAccount?.key} target="_blank" rel="noreferrer" style={{ color: '#666' }}>{formatFollowers(ytFollowers)}</a>
                                                : <span style={{ color: '#d9d9d9' }}>—</span>}
                                        </td>}
                                        {/* IG followers */}
                                        {col('ig') && <td style={{ ...tdStyle, textAlign: 'center' }}>
                                            {igFollowers != null
                                                ? <a href={igAccount?.key} target="_blank" rel="noreferrer" style={{ color: '#666' }}>{formatFollowers(igFollowers)}</a>
                                                : <span style={{ color: '#d9d9d9' }}>—</span>}
                                        </td>}
                                        {/* TT followers */}
                                        {col('tt') && <td style={{ ...tdStyle, textAlign: 'center' }}>
                                            {ttFollowers != null
                                                ? <a href={ttAccount?.key} target="_blank" rel="noreferrer" style={{ color: '#666' }}>{formatFollowers(ttFollowers)}</a>
                                                : <span style={{ color: '#d9d9d9' }}>—</span>}
                                        </td>}
                                        {/* budget */}
                                        {col('budget') && <td style={tdStyle}>
                                            <InputNumber
                                                size="small"
                                                style={{ width: 120 }}
                                                prefix="€"
                                                min={0}
                                                step={100}
                                                precision={0}
                                                value={meta.budget ?? undefined}
                                                onChange={(val) =>
                                                    setMetaMap((prev) => ({
                                                        ...prev,
                                                        [c.id]: { ...prev[c.id] ?? { comments: '', approved: null, pillar: '' }, budget: val ?? null },
                                                    }))
                                                }
                                                onBlur={() => saveMeta(meta.budget, meta.comments, meta.approved, meta.pillar)}
                                            />
                                        </td>}
                                        {/* comments */}
                                        {col('comments') && <td style={tdStyle}>
                                            <Input.TextArea
                                                size="small"
                                                autoSize={{ minRows: 1, maxRows: 3 }}
                                                style={{ width: 220, fontSize: 12, resize: 'none' }}
                                                value={meta.comments}
                                                onChange={(e) =>
                                                    setMetaMap((prev) => ({
                                                        ...prev,
                                                        [c.id]: { ...prev[c.id] ?? { budget: null, approved: null, pillar: '' }, comments: e.target.value },
                                                    }))
                                                }
                                                onBlur={() => saveMeta(meta.budget, meta.comments, meta.approved, meta.pillar)}
                                            />
                                        </td>}
                                        {/* deliverables */}
                                        {col('deliverables') && <td style={tdStyle}>
                                            {campaign?.id && (
                                                <CreatorDeliverables
                                                    campaignId={campaign.id}
                                                    creatorId={c.id}
                                                    deliverables={(campaign.creator_deliverables ?? []).filter(
                                                        (d) => d.creator_id === c.id,
                                                    )}
                                                    onSaved={refetchCampaign}
                                                />
                                            )}
                                        </td>}
                                        {/* remove */}
                                        <td style={tdStyle}>
                                            <Button
                                                danger
                                                type="text"
                                                size="small"
                                                icon={<DeleteOutlined />}
                                                onClick={() => handleRemoveCreator(c.id)}
                                            />
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                )}
            </div>
        </PageContainer>
        <CreatorDrawer
            creatorId={drawerCreatorId}
            onClose={() => setDrawerCreatorId(null)}
        />
        </>
    );
};

export default CampaignView;
